<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import mermaid  from 'mermaid'
import Panzoom  from '@panzoom/panzoom'
import { useQuestionsStore } from 'stores/questions.js'
import { useQuestionTypes }  from 'composables/useQuestionTypes.js'
import { useConditions }     from 'composables/useConditions.js'

const props = defineProps({
  items:     { type: Array,  default: () => [] },
  eventName: { type: String, default: ''       },
})
const emit = defineEmits(['edit-question-logic'])

const store           = useQuestionsStore()
const activeQuestions = computed(() => store.questions.filter(q => q.state === 'active'))
const { typeLabel }               = useQuestionTypes()
const { OPERATOR_LABELS, hasConditions } = useConditions()

function questionById(id) {
  return activeQuestions.value.find(q => q.id === id)
}

// ── Action config ─────────────────────────────────────────────────────────────
const ACTION = {
  show:     { label: 'Show',     hex: '#3b82f6', fill: '#eff6ff', textColor: '#1d4ed8' },
  hide:     { label: 'Hide',     hex: '#6b7280', fill: '#f1f5f9', textColor: '#374151' },
  require:  { label: 'Required', hex: '#10b981', fill: '#f0fdf4', textColor: '#065f46' },
  block:    { label: 'Block',    hex: '#ef4444', fill: '#fef2f2', textColor: '#991b1b' },
  redirect: { label: 'Redirect', hex: '#f97316', fill: '#fff7ed', textColor: '#9a3412' },
}

// ── Derived state ─────────────────────────────────────────────────────────────
const itemIdToIdx = computed(() => {
  const m = new Map()
  props.items.forEach((it, i) => { if (it.id) m.set(it.id, i) })
  return m
})

const totalConditions = computed(() =>
  props.items.reduce((n, it) => n + (it.conditions?.rules?.length ?? 0), 0)
)

// ── Mermaid graph builder ─────────────────────────────────────────────────────
function safeName(text = '', maxLen = 38) {
  return text.replace(/["""\\<>[\]]/g, "'").replace(/[\r\n]+/g, ' ').trim().slice(0, maxLen)
}

function buildDef() {
  const lines = ['graph TD']
  let edgeIdx = 0
  const linkStyles = []

  // ── 1. Question nodes ─────────────────────────────────────────────────────
  props.items.forEach((item, idx) => {
    const q     = questionById(item.question_id)
    const title = safeName(q?.title ?? 'Unknown question')
    const type  = q ? typeLabel(q.question_type) : ''
    lines.push(`  Q${idx}["#${idx + 1} · ${title}\\n${type}"]`)
  })

  // ── 2. AND / OR gate nodes — one per question with 2+ resolvable rules ────
  //    Diamond shape {text} signals a decision point; labelled ANY (OR) / ALL (AND)
  props.items.forEach((targetItem, tgtIdx) => {
    const rules      = targetItem.conditions?.rules ?? []
    const validRules = rules.filter(r => itemIdToIdx.value.get(r.source_event_question_id) !== undefined)
    if (validRules.length < 2) return
    const match  = targetItem.conditions?.match ?? 'any'
    const action = targetItem.conditions?.action ?? 'show'
    const cfg    = ACTION[action] ?? ACTION.show
    const label  = match === 'all' ? 'ALL (AND)' : 'ANY (OR)'
    lines.push(`  GATE_Q${tgtIdx}{"${label}"}`)
    lines.push(`  style GATE_Q${tgtIdx} fill:${cfg.fill},stroke:${cfg.hex},color:${cfg.textColor},stroke-width:2px`)
  })

  // ── 3. Edges ──────────────────────────────────────────────────────────────
  props.items.forEach((targetItem, tgtIdx) => {
    const rules      = targetItem.conditions?.rules ?? []
    const action     = targetItem.conditions?.action ?? 'show'
    const cfg        = ACTION[action] ?? ACTION.show
    const validRules = rules.filter(r => itemIdToIdx.value.get(r.source_event_question_id) !== undefined)

    if (validRules.length === 0) return

    if (validRules.length === 1) {
      // Single condition → direct labelled edge (existing behaviour)
      const rule    = validRules[0]
      const srcIdx  = itemIdToIdx.value.get(rule.source_event_question_id)
      const opLabel = OPERATOR_LABELS[rule.operator] ?? rule.operator
      const valStr  = rule.value ? ` '${safeName(rule.value, 20)}'` : ''
      lines.push(`  Q${srcIdx} -->|"${cfg.label} if ${opLabel}${valStr}"| Q${tgtIdx}`)
      linkStyles.push(`  linkStyle ${edgeIdx} stroke:${cfg.hex},stroke-width:2.5px`)
      edgeIdx++
    } else {
      // Multiple conditions → each source feeds the AND/OR gate (dashed),
      // then the gate fires the action to the target (solid).
      validRules.forEach(rule => {
        const srcIdx  = itemIdToIdx.value.get(rule.source_event_question_id)
        const opLabel = OPERATOR_LABELS[rule.operator] ?? rule.operator
        const valStr  = rule.value ? ` '${safeName(rule.value, 20)}'` : ''
        lines.push(`  Q${srcIdx} -->|"${opLabel}${valStr}"| GATE_Q${tgtIdx}`)
        linkStyles.push(`  linkStyle ${edgeIdx} stroke:${cfg.hex},stroke-width:1.5px,stroke-dasharray:5 3`)
        edgeIdx++
      })
      lines.push(`  GATE_Q${tgtIdx} -->|"${cfg.label}"| Q${tgtIdx}`)
      linkStyles.push(`  linkStyle ${edgeIdx} stroke:${cfg.hex},stroke-width:2.5px`)
      edgeIdx++
    }
  })

  // ── 4. Question node styles ───────────────────────────────────────────────
  props.items.forEach((item, idx) => {
    if (hasConditions(item)) {
      const cfg = ACTION[item.conditions.action] ?? ACTION.show
      lines.push(`  style Q${idx} stroke:${cfg.hex},stroke-width:2.5px,fill:#fafbff,color:#1e293b,rx:10`)
    } else {
      lines.push(`  style Q${idx} stroke:#e2e8f0,stroke-width:1.5px,fill:#ffffff,color:#374151,rx:10`)
    }
  })

  lines.push(...linkStyles)
  return lines.join('\n')
}

// ── Mermaid render ────────────────────────────────────────────────────────────
const svgContent  = ref('')
const renderError = ref('')
let   renderCount = 0

async function renderDiagram() {
  if (!props.items.length || totalConditions.value === 0) {
    svgContent.value = ''
    renderError.value = ''
    return
  }

  const isDark = document.documentElement.classList.contains('dark')

  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: {
      primaryColor:        isDark ? '#1e2a3a' : '#ffffff',
      primaryBorderColor:  isDark ? '#374151' : '#e2e8f0',
      primaryTextColor:    isDark ? '#e2e8f0' : '#1e293b',
      background:          isDark ? '#0d1117' : '#f8fafc',
      mainBkg:             isDark ? '#1e2a3a' : '#ffffff',
      nodeBorder:          isDark ? '#374151' : '#d1d5db',
      lineColor:           isDark ? '#64748b' : '#94a3b8',
      edgeLabelBackground: isDark ? '#1e2a3a' : '#f8fafc',
      fontSize:            '14px',
      fontFamily:          'ui-sans-serif, system-ui, sans-serif',
    },
    flowchart: {
      curve: 'basis', htmlLabels: false, useMaxWidth: false,
      diagramPadding: 32, rankSpacing: 90, nodeSpacing: 70,
    },
  })

  renderCount++
  const id = `mermaid-logic-${renderCount}`

  try {
    const { svg } = await mermaid.render(id, buildDef())
    // Strip mermaid's inline max-width so CSS / panzoom controls the size
    svgContent.value = svg
      .replace(/\s+style="[^"]*max-width[^"]*"/g, '')
      .replace(/(<svg\b[^>]*)\s+width="[^"]*"/g, '$1 width="100%"')
    renderError.value = ''
  } catch (e) {
    console.error('[LogicCanvas] mermaid error', e)
    renderError.value = 'Could not render the diagram.'
    svgContent.value  = ''
  }
}

// ── Panzoom ───────────────────────────────────────────────────────────────────
const inlineWrapRef     = ref(null)
const inlinePzRef       = ref(null)
const fullscreenWrapRef = ref(null)
const fullscreenPzRef   = ref(null)

const inlineScale     = ref(100)
const fullscreenScale = ref(100)

let inlinePz     = null
let fullscreenPz = null

// WeakMap stores the bound wheel handler per panzoom instance for clean removal
const _wheelHandlers = new WeakMap()

function createPz(innerEl, outerEl, scaleRef) {
  if (!innerEl || !outerEl) return null

  const pz = Panzoom(innerEl, {
    maxScale: 8,
    minScale: 0.1,
    step:     0.2,
    // No 'contain' — allows unrestricted pan after zoom
    // No 'canvas' — that flag is for SVG elements, not divs
  })

  // Bound wheel handler stored so we can remove it precisely on destroy
  const onWheel = (e) => pz.zoomWithWheel(e)
  outerEl.addEventListener('wheel', onWheel, { passive: false })
  _wheelHandlers.set(pz, { outerEl, onWheel })

  // Keep the scale counter in sync
  innerEl.addEventListener('panzoomzoom', () => {
    scaleRef.value = Math.round(pz.getScale() * 100)
  })

  return pz
}

function destroyPz(pz) {
  if (!pz) return null
  const stored = _wheelHandlers.get(pz)
  if (stored) {
    stored.outerEl.removeEventListener('wheel', stored.onWheel)
    _wheelHandlers.delete(pz)
  }
  pz.destroy()
  return null
}

// flush:'post' ensures the watch fires AFTER Vue has updated the DOM,
// so inlinePzRef / inlineWrapRef are guaranteed to be populated.
watch(svgContent, () => {
  inlinePz = destroyPz(inlinePz)
  inlineScale.value = 100
  if (inlinePzRef.value && inlineWrapRef.value) {
    inlinePz = createPz(inlinePzRef.value, inlineWrapRef.value, inlineScale)
  }
}, { flush: 'post' })

// ── Zoom controls ─────────────────────────────────────────────────────────────
function zoomIn(fs = false)    { fs ? fullscreenPz?.zoomIn()  : inlinePz?.zoomIn() }
function zoomOut(fs = false)   { fs ? fullscreenPz?.zoomOut() : inlinePz?.zoomOut() }
function resetZoom(fs = false) {
  if (fs) { fullscreenPz?.reset(); fullscreenScale.value = 100 }
  else    { inlinePz?.reset();     inlineScale.value = 100 }
}

// ── Download ──────────────────────────────────────────────────────────────────
const showDownloadMenu = ref(false)

function buildFilename(ext) {
  const slug = (props.eventName || 'event')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${slug}-conditional-logic.${ext}`
}

async function downloadDiagram(format) {
  showDownloadMenu.value = false
  if (!svgContent.value) return

  // Prefer fullscreen SVG element when open (larger rendered size = better PNG)
  const svgEl = (fullscreen.value ? fullscreenPzRef.value : inlinePzRef.value)?.querySelector('svg')
  if (!svgEl) return

  const { width, height } = svgEl.getBoundingClientRect()
  if (!width || !height) return

  if (format === 'svg') {
    const clone = svgEl.cloneNode(true)
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    clone.setAttribute('width',  Math.round(width))
    clone.setAttribute('height', Math.round(height))
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' })
    triggerDownload(URL.createObjectURL(blob), buildFilename('svg'))
    return
  }

  // Scale up so the long side is at least 2000px — prevents blurriness when
  // downloading from the small inline panel (which may only be ~400px tall).
  // Cap at 5× to avoid unreasonably large files from the fullscreen view.
  const scale = Math.min(5, Math.max(2, Math.ceil(2000 / Math.max(width, height))))
  const w = Math.round(width  * scale)
  const h = Math.round(height * scale)

  const pngClone = svgEl.cloneNode(true)
  pngClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  pngClone.setAttribute('width',  w)
  pngClone.setAttribute('height', h)
  // Base64 data URI avoids the tainted-canvas restriction that blob: URLs
  // trigger in Chrome when the SVG contains an embedded <style> block.
  const svgStr  = new XMLSerializer().serializeToString(pngClone)
  const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`

  const img = new Image()
  await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = dataUri })

  const canvas = document.createElement('canvas')
  canvas.width  = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#f8fafc'
  ctx.fillRect(0, 0, w, h)
  ctx.drawImage(img, 0, 0)
  canvas.toBlob(blob => triggerDownload(URL.createObjectURL(blob), buildFilename('png')), 'image/png')
}

function triggerDownload(url, filename) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

function onDocClick(e) {
  if (!e.target.closest('[data-download-menu]')) showDownloadMenu.value = false
}

// ── Fullscreen ────────────────────────────────────────────────────────────────
const fullscreen = ref(false)

async function openFullscreen() {
  fullscreen.value = true
  document.body.style.overflow = 'hidden'
  // Wait for the fullscreen DOM to mount, then init panzoom
  await nextTick()
  await nextTick()
  fullscreenPz = destroyPz(fullscreenPz)
  fullscreenScale.value = 100
  if (fullscreenPzRef.value && fullscreenWrapRef.value) {
    fullscreenPz = createPz(fullscreenPzRef.value, fullscreenWrapRef.value, fullscreenScale)
  }
}

function closeFullscreen() {
  fullscreen.value = false
  document.body.style.overflow = ''
  fullscreenPz = destroyPz(fullscreenPz)
  fullscreenScale.value = 100
}

function onKeydown(e) {
  if (e.key === 'Escape') {
    if (fullscreen.value) closeFullscreen()
    showDownloadMenu.value = false
  }
}

onMounted(() => {
  nextTick(renderDiagram)
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('click',   onDocClick)
})

onUnmounted(() => {
  inlinePz     = destroyPz(inlinePz)
  fullscreenPz = destroyPz(fullscreenPz)
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click',   onDocClick)
  document.body.style.overflow = ''
})

watch(() => props.items, () => nextTick(renderDiagram), { deep: true })
</script>

<template>
  <!-- ── Inline canvas ─────────────────────────────────────────────────────── -->
  <div class="flex flex-col h-full min-h-0 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-[#0d1117]">

    <!-- Toolbar -->
    <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shrink-0">
      <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 shrink-0">Logic flow</p>
      <div class="flex-1" />

      <!-- Zoom controls (inline) -->
      <template v-if="svgContent">
        <div class="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          <button type="button" @click="zoomOut(false)"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-bold text-base"
                  title="Zoom out (−)">−</button>
          <button type="button" @click="resetZoom(false)"
                  class="px-2 h-7 text-[10px] font-bold tabular-nums text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors min-w-[44px] text-center"
                  title="Reset zoom">{{ inlineScale }}%</button>
          <button type="button" @click="zoomIn(false)"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-bold text-base"
                  title="Zoom in (+)">+</button>
        </div>

        <!-- Download -->
        <div data-download-menu class="relative">
          <button type="button" @click.stop="showDownloadMenu = !showDownloadMenu"
                  class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                  title="Download diagram">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
            </svg>
          </button>
          <div v-if="showDownloadMenu"
               class="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden min-w-[130px]">
            <button type="button" @click.stop="downloadDiagram('png')"
                    class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
              <svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
              </svg>
              PNG image
            </button>
            <div class="h-px bg-gray-100 dark:bg-gray-700" />
            <button type="button" @click.stop="downloadDiagram('svg')"
                    class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
              <svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
              </svg>
              SVG vector
            </button>
          </div>
        </div>

        <!-- Fullscreen -->
        <button type="button" @click="openFullscreen"
                class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                title="Full screen">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/>
          </svg>
        </button>
      </template>
    </div>

    <!-- Diagram body -->
    <div class="relative flex-1 min-h-0 flex items-center justify-center">

      <!-- Empty -->
      <div v-if="items.length === 0" class="text-center px-6">
        <p class="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">No questions yet</p>
        <p class="text-xs text-gray-400 dark:text-gray-500">Add questions in the Questions tab first.</p>
      </div>

      <!-- No conditions -->
      <div v-else-if="totalConditions === 0" class="flex flex-col items-center gap-3 text-center px-6">
        <div class="w-12 h-12 rounded-2xl bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center">
          <svg class="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">No conditions yet</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 max-w-[220px]">Open a question card in the Questions tab and click the branch icon.</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="renderError" class="text-center px-6">
        <p class="text-sm font-semibold text-red-500 dark:text-red-400 mb-1">Render error</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 max-w-xs">{{ renderError }}</p>
      </div>

      <!-- Panzoom wrapper (inline) -->
      <div v-else
           ref="inlineWrapRef"
           class="w-full h-full overflow-hidden select-none"
           style="touch-action: none; cursor: grab">
        <div ref="inlinePzRef"
             class="mermaid-wrap"
             v-html="svgContent" />
      </div>

      <!-- Legend overlay — bottom-left of canvas, never wraps the toolbar -->
      <div v-if="svgContent"
           class="absolute bottom-3 left-3 z-10 pointer-events-none flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 rounded-xl bg-white/90 dark:bg-[#1C2128]/90 border border-gray-200/80 dark:border-gray-700/80 shadow-sm backdrop-blur-sm">
        <div v-for="(cfg, action) in ACTION" :key="action" class="flex items-center gap-1.5">
          <div class="w-3.5 h-[2px] rounded-full shrink-0" :style="{ backgroundColor: cfg.hex }" />
          <span class="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{{ cfg.label }}</span>
        </div>
      </div>

    </div>
  </div>

  <!-- ── Fullscreen overlay ─────────────────────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="fs">
      <div v-if="fullscreen"
           class="fixed inset-0 z-[9999] bg-gray-50 dark:bg-[#0d1117] flex flex-col">

        <!-- Fullscreen toolbar -->
        <div class="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shrink-0">
          <p class="text-sm font-bold text-gray-700 dark:text-gray-200 shrink-0">Logic flow</p>
          <div class="flex-1" />

          <!-- Zoom controls (fullscreen) -->
          <div class="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button type="button" @click="zoomOut(true)"
                    class="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-bold text-lg"
                    title="Zoom out">−</button>
            <button type="button" @click="resetZoom(true)"
                    class="px-2.5 h-8 text-xs font-bold tabular-nums text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors min-w-[52px] text-center"
                    title="Reset zoom">{{ fullscreenScale }}%</button>
            <button type="button" @click="zoomIn(true)"
                    class="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-bold text-lg"
                    title="Zoom in">+</button>
          </div>

          <!-- Download (fullscreen) -->
          <div data-download-menu class="relative">
            <button type="button" @click.stop="showDownloadMenu = !showDownloadMenu"
                    class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-violet-50 dark:hover:bg-violet-900/20 text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    title="Download diagram">
              <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
              </svg>
            </button>
            <div v-if="showDownloadMenu"
                 class="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden min-w-[130px]">
              <button type="button" @click.stop="downloadDiagram('png')"
                      class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                <svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                </svg>
                PNG image
              </button>
              <div class="h-px bg-gray-100 dark:bg-gray-700" />
              <button type="button" @click.stop="downloadDiagram('svg')"
                      class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                <svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
                </svg>
                SVG vector
              </button>
            </div>
          </div>

          <!-- Close -->
          <button type="button" @click="closeFullscreen"
                  class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  title="Close (Esc)">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Fullscreen diagram -->
        <div ref="fullscreenWrapRef"
             class="relative flex-1 overflow-hidden select-none"
             style="touch-action: none; cursor: grab">
          <div ref="fullscreenPzRef"
               class="mermaid-wrap mermaid-wrap--fs"
               v-html="svgContent" />

          <!-- Legend overlay -->
          <div class="absolute bottom-4 left-4 z-10 pointer-events-none flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2 rounded-xl bg-white/90 dark:bg-[#1C2128]/90 border border-gray-200/80 dark:border-gray-700/80 shadow-md backdrop-blur-sm">
            <div v-for="(cfg, action) in ACTION" :key="action" class="flex items-center gap-2">
              <div class="w-5 h-[2.5px] rounded-full shrink-0" :style="{ backgroundColor: cfg.hex }" />
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">{{ cfg.label }}</span>
            </div>
          </div>
        </div>

        <!-- Hints -->
        <div class="shrink-0 flex items-center justify-center gap-6 py-2 border-t border-gray-100 dark:border-gray-800">
          <span class="text-[11px] text-gray-400 dark:text-gray-600">
            Scroll to zoom · Drag to pan · Pinch to zoom
          </span>
          <span class="text-[11px] text-gray-400 dark:text-gray-600">
            <kbd class="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-[10px] font-mono">Esc</kbd> to close
          </span>
        </div>

      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Inline: fill the panel */
.mermaid-wrap :deep(svg) {
  display: block;
  width: 100%;
  height: auto;
  min-width: 400px;
}

/* Fullscreen: larger base size */
.mermaid-wrap--fs :deep(svg) {
  min-width: 800px;
  min-height: 80vh;
}

/* Consistent fonts */
.mermaid-wrap :deep(.nodeLabel),
.mermaid-wrap :deep(.label),
.mermaid-wrap :deep(.edgeLabel),
.mermaid-wrap :deep(.edgeLabel p) {
  font-family: ui-sans-serif, system-ui, sans-serif;
}

/* Fullscreen transition */
.fs-enter-active { transition: opacity 0.16s ease; }
.fs-leave-active { transition: opacity 0.12s ease; }
.fs-enter-from,
.fs-leave-to     { opacity: 0; }
</style>
